// vg-binding: P7.D-99
//
// apps/vendor/src/features/dashboard/CapacityMeter.tsx
//
// Radial capacity meter — colour-tiered (green < 80, yellow
// 80..95, red >= 95). Uses an inline SVG ring instead of pulling a chart
// library for one widget.

import { useTranslation } from 'react-i18next';

import { cn } from '@printway/ui';

import type { CapacityMeter as CapacityData } from './api.js';

interface Props {
  data: CapacityData | undefined;
  isLoading?: boolean;
}

const RING_COLOR: Record<CapacityData['tier'], string> = {
  green: 'stroke-green-500',
  yellow: 'stroke-yellow-500',
  red: 'stroke-red-500',
  unknown: 'stroke-pw-border',
};

const TEXT_COLOR: Record<CapacityData['tier'], string> = {
  green: 'text-green-600',
  yellow: 'text-yellow-600',
  red: 'text-red-600',
  unknown: 'text-pw-text-muted',
};

const RADIUS = 48;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS; // ~301.6

export function CapacityMeter({ data, isLoading }: Props): JSX.Element {
  const { t } = useTranslation('dashboard');
  if (isLoading || !data) {
    return (
      <section className="flex h-40 items-center justify-center rounded-xl border border-pw-border bg-pw-surface">
        <div
          aria-hidden="true"
          className="h-8 w-8 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary"
        />
      </section>
    );
  }

  const pct = Math.max(0, Math.min(100, data.utilisation_percent));
  const dashOffset = CIRCUMFERENCE * (1 - pct / 100);

  return (
    <section
      data-testid="dashboard-capacity-meter"
      className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
    >
      <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('capacity.title')}</h3>
      <div className="flex items-center gap-4">
        <svg viewBox="0 0 120 120" className="h-28 w-28 -rotate-90">
          <circle
            cx="60"
            cy="60"
            r={RADIUS}
            fill="none"
            strokeWidth="10"
            className="stroke-pw-surface"
          />
          <circle
            cx="60"
            cy="60"
            r={RADIUS}
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            className={cn('transition-all', RING_COLOR[data.tier])}
          />
        </svg>
        <div className="min-w-0">
          <p className={cn('text-3xl font-semibold', TEXT_COLOR[data.tier])}>
            {pct}%
          </p>
          <p className="mt-0.5 text-xs text-pw-text-muted">
            {t('capacity.unitsLabel', { current: data.current_load, capacity: data.capacity_global })}
          </p>
          {data.tier === 'red' ? (
            <p className="mt-1 text-xs font-medium text-red-700">
              {t('capacity.tierRed')}
            </p>
          ) : data.tier === 'yellow' ? (
            <p className="mt-1 text-xs font-medium text-yellow-700">
              {t('capacity.tierYellow', { percent: pct })}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default CapacityMeter;
